import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { SimpleTextInput } from "./FormTextInput";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: "500px",
    padding: "24px",
  },
  dialogActions: {
    padding: "16px 24px",
  },
  noteBox: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "4px",
    padding: "12px",
    marginBottom: "16px",
  },
  noteText: {
    fontSize: "12px",
    color: "#856404",
    margin: 0,
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "8px",
    marginTop: "16px",
  },
  cancelButton: {
    color: theme.palette.text.secondary,
  },
  launchButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
}));

interface ExtraDataFieldConfig {
  fieldName: string;
  id: string;
  type: string;
}

// Configuration for extraData fields - can be extended in the future
const EXTRA_DATA_FIELDS: ExtraDataFieldConfig[] = [
  { fieldName: "Account ID of Player", id: "accountId", type: "textBox" },
  { fieldName: "Session ID", id: "sessionId", type: "textBox" },
];

interface TestLaunchDialogProps {
  open: boolean;
  onClose: () => void;
  onLaunch: (data: any, isDebugBuild: boolean) => void;
  loading?: boolean;
}

const TestLaunchDialog: React.FC<TestLaunchDialogProps> = ({
  open,
  onClose,
  onLaunch,
  loading = false,
}) => {
  const classes = useStyles();

  const [formData, setFormData] = useState({
    countryCode: "",
    lang: "",
    currency: "",
    extraData: {} as Record<string, string>,
  });
  const [isDebugBuild, setIsDebugBuild] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleExtraDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      extraData: {
        ...prev.extraData,
        [name]: value,
      },
    }));
  };

  const handleLaunch = () => {
    // Filter out empty values
    const payload: any = {};

    if (formData.countryCode) payload.countryCode = formData.countryCode;
    if (formData.lang) payload.lang = formData.lang;
    if (formData.currency) payload.currency = formData.currency;

    // Only include extraData if there are non-empty values
    const extraDataEntries = Object.entries(formData.extraData).filter(
      ([_, value]) => value !== ""
    );
    if (extraDataEntries.length > 0) {
      payload.extraData = Object.fromEntries(extraDataEntries);
    }

    onLaunch(payload, isDebugBuild);
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      countryCode: "",
      lang: "",
      currency: "",
      extraData: {},
    });
    setIsDebugBuild(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Test Game Launch</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.noteBox}>
          <Typography className={classes.noteText}>
            <strong>Note:</strong> The countryCode, currency, and lang fields
            will be overridden by what the operator returns. These fields will
            only be honored if the operator doesn't return any country/currency
            or lang.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SimpleTextInput
              label="Country Code"
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              placeholder="e.g., US, GB, IN"
            />
          </Grid>

          <Grid item xs={12}>
            <SimpleTextInput
              label="Language"
              name="lang"
              value={formData.lang}
              onChange={handleChange}
              placeholder="e.g., en, es, fr"
            />
          </Grid>

          <Grid item xs={12}>
            <SimpleTextInput
              label="Currency"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              placeholder="e.g., USD, GBP, EUR"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>
              Extra Data (Optional)
            </Typography>
          </Grid>

          {EXTRA_DATA_FIELDS.map((field) => (
            <Grid item xs={12} key={field.id}>
              <SimpleTextInput
                label={field.fieldName}
                name={field.id}
                value={formData.extraData[field.id] || ""}
                onChange={handleExtraDataChange}
                placeholder={`Enter ${field.fieldName}`}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDebugBuild}
                  onChange={(e) => setIsDebugBuild(e.target.checked)}
                  color="primary"
                />
              }
              label="Debug Build"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
          className={classes.cancelButton}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleLaunch}
          className={classes.launchButton}
          disabled={loading}
        >
          Launch Game
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestLaunchDialog;
